import { defineMessages } from '@/i18n'

/** Sign-in (landing page and the plain login form) and the no-access screen. */
export const authMessages = defineMessages({
  en: {
    tagline: 'Survey · Map · Connect',
    heroInfrastructure: 'Infrastructure.',
    heroMeasured: 'Measured.',
    heroConnected: 'Connected.',
    heroBody: 'Plan, measure and manage fiber infrastructure — from field data to final GIS export.',
    loginSubtitle: 'Sign in with your AU account.',
    username: 'Username',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    keepSignedIn: 'Keep me signed in',
    forgotPassword: 'Forgot password?',
    forgotHelp: 'Passwords are managed in your AU account — contact an administrator to reset yours.',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    secureAccess: 'Secure access for authorized users',
    whatsInside: "What's inside",
    capRtkTitle: 'RTK survey & mapping',
    capRtkBody: 'Field measurements on an interactive map — routes, fix points and site photos, editable in place.',
    capAufmassTitle: 'Aufmaß reporting grid',
    capAufmassBody: 'Live crew-day reporting over the construction database — quantities, hours and corrections.',
    capDashboardTitle: 'Dashboards & bonus',
    capDashboardBody: 'Operations at a glance — trends, top projects and crews, and the worker bonus calculator.',
    capExportTitle: 'GeoJSON, GeoPackage & PDF',
    capExportBody: 'Export in the CRS you need, or generate a per-project dig-up report — field data to final GIS.',
    errTooMany: 'Too many attempts. Wait a minute and try again.',
    errWrongCredentials: 'Wrong username or password.',
    errServer: 'Server error during login ({status}). This is not your password.',
    errGeneric: 'Could not sign in. Please try again.',
    noAccessTitle: 'No access yet',
    noAccessBody: "You're signed in as {name}, but this page hasn't been granted to your account. Ask an administrator to give you access.",
  },
  de: {
    tagline: 'Vermessen · Kartieren · Verbinden',
    heroInfrastructure: 'Infrastruktur.',
    heroMeasured: 'Vermessen.',
    heroConnected: 'Vernetzt.',
    heroBody: 'Glasfaserinfrastruktur planen, vermessen und verwalten — von den Felddaten bis zum fertigen GIS-Export.',
    loginSubtitle: 'Melden Sie sich mit Ihrem AU-Konto an.',
    username: 'Benutzername',
    password: 'Passwort',
    showPassword: 'Passwort anzeigen',
    hidePassword: 'Passwort verbergen',
    keepSignedIn: 'Angemeldet bleiben',
    forgotPassword: 'Passwort vergessen?',
    forgotHelp: 'Passwörter werden in Ihrem AU-Konto verwaltet — wenden Sie sich zum Zurücksetzen an einen Administrator.',
    signIn: 'Anmelden',
    signingIn: 'Anmeldung läuft…',
    secureAccess: 'Sicherer Zugang für berechtigte Benutzer',
    whatsInside: 'Funktionen',
    capRtkTitle: 'RTK-Vermessung & Kartierung',
    capRtkBody: 'Feldmessungen auf einer interaktiven Karte — Trassen, Festpunkte und Baustellenfotos, direkt bearbeitbar.',
    capAufmassTitle: 'Aufmaß-Berichtstabelle',
    capAufmassBody: 'Kolonnentage live aus der Baudatenbank — Mengen, Stunden und Korrekturen.',
    capDashboardTitle: 'Dashboards & Bonus',
    capDashboardBody: 'Der Betrieb auf einen Blick — Trends, Top-Projekte und -Kolonnen sowie der Bonusrechner für Arbeiter.',
    capExportTitle: 'GeoJSON, GeoPackage & PDF',
    capExportBody: 'Export im benötigten Koordinatensystem oder ein Aufgrabungsbericht pro Projekt — von den Felddaten bis zum fertigen GIS.',
    errTooMany: 'Zu viele Versuche. Bitte warten Sie eine Minute und versuchen Sie es erneut.',
    errWrongCredentials: 'Benutzername oder Passwort ist falsch.',
    errServer: 'Serverfehler bei der Anmeldung ({status}). Es liegt nicht an Ihrem Passwort.',
    errGeneric: 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    noAccessTitle: 'Noch kein Zugriff',
    noAccessBody: 'Sie sind als {name} angemeldet, aber diese Seite wurde für Ihr Konto noch nicht freigegeben. Bitten Sie einen Administrator um Zugriff.',
  },
  cnr: {
    tagline: 'Snimi · Mapiraj · Poveži',
    heroInfrastructure: 'Infrastruktura.',
    heroMeasured: 'Izmjerena.',
    heroConnected: 'Povezana.',
    heroBody: 'Planirajte, mjerite i upravljajte optičkom infrastrukturom — od terenskih podataka do konačnog GIS izvoza.',
    loginSubtitle: 'Prijavite se svojim AU nalogom.',
    username: 'Korisničko ime',
    password: 'Lozinka',
    showPassword: 'Prikaži lozinku',
    hidePassword: 'Sakrij lozinku',
    keepSignedIn: 'Zapamti prijavu',
    forgotPassword: 'Zaboravljena lozinka?',
    forgotHelp: 'Lozinkama se upravlja u vašem AU nalogu — obratite se administratoru da vam je resetuje.',
    signIn: 'Prijava',
    signingIn: 'Prijavljivanje…',
    secureAccess: 'Siguran pristup za ovlašćene korisnike',
    whatsInside: 'Šta sadrži',
    capRtkTitle: 'RTK snimanje i mapiranje',
    capRtkBody: 'Terenska mjerenja na interaktivnoj mapi — trase, fiksne tačke i fotografije s gradilišta, s izmjenama na licu mjesta.',
    capAufmassTitle: 'Aufmaß tabela izvještaja',
    capAufmassBody: 'Izvještavanje po danima kolona uživo iz baze gradnje — količine, sati i ispravke.',
    capDashboardTitle: 'Pregledi i bonus',
    capDashboardBody: 'Poslovanje na prvi pogled — trendovi, najbolji projekti i kolone te kalkulator bonusa za radnike.',
    capExportTitle: 'GeoJSON, GeoPackage i PDF',
    capExportBody: 'Izvezite u potrebnom koordinatnom sistemu ili napravite izvještaj o iskopu po projektu — od terenskih podataka do konačnog GIS-a.',
    errTooMany: 'Previše pokušaja. Sačekajte minut i pokušajte ponovo.',
    errWrongCredentials: 'Pogrešno korisničko ime ili lozinka.',
    errServer: 'Greška servera pri prijavi ({status}). Nije do vaše lozinke.',
    errGeneric: 'Prijava nije uspjela. Pokušajte ponovo.',
    noAccessTitle: 'Još nemate pristup',
    noAccessBody: 'Prijavljeni ste kao {name}, ali ova stranica još nije dodijeljena vašem nalogu. Zamolite administratora da vam omogući pristup.',
  },
  bs: {
    tagline: 'Snimi · Mapiraj · Poveži',
    heroInfrastructure: 'Infrastruktura.',
    heroMeasured: 'Izmjerena.',
    heroConnected: 'Povezana.',
    heroBody: 'Planirajte, mjerite i upravljajte optičkom infrastrukturom — od terenskih podataka do konačnog GIS izvoza.',
    loginSubtitle: 'Prijavite se svojim AU računom.',
    username: 'Korisničko ime',
    password: 'Lozinka',
    showPassword: 'Prikaži lozinku',
    hidePassword: 'Sakrij lozinku',
    keepSignedIn: 'Zapamti prijavu',
    forgotPassword: 'Zaboravljena lozinka?',
    forgotHelp: 'Lozinkama se upravlja u vašem AU računu — obratite se administratoru da vam je resetuje.',
    signIn: 'Prijava',
    signingIn: 'Prijavljivanje…',
    secureAccess: 'Siguran pristup za ovlaštene korisnike',
    whatsInside: 'Šta sadrži',
    capRtkTitle: 'RTK snimanje i mapiranje',
    capRtkBody: 'Terenska mjerenja na interaktivnoj mapi — trase, fiksne tačke i fotografije s gradilišta, s izmjenama na licu mjesta.',
    capAufmassTitle: 'Aufmaß tabela izvještaja',
    capAufmassBody: 'Izvještavanje po danima kolona uživo iz baze gradnje — količine, sati i ispravke.',
    capDashboardTitle: 'Pregledi i bonus',
    capDashboardBody: 'Poslovanje na prvi pogled — trendovi, najbolji projekti i kolone te kalkulator bonusa za radnike.',
    capExportTitle: 'GeoJSON, GeoPackage i PDF',
    capExportBody: 'Izvezite u potrebnom koordinatnom sistemu ili napravite izvještaj o iskopu po projektu — od terenskih podataka do konačnog GIS-a.',
    errTooMany: 'Previše pokušaja. Pričekajte minutu i pokušajte ponovo.',
    errWrongCredentials: 'Pogrešno korisničko ime ili lozinka.',
    errServer: 'Greška servera pri prijavi ({status}). Nije do vaše lozinke.',
    errGeneric: 'Prijava nije uspjela. Pokušajte ponovo.',
    noAccessTitle: 'Još nemate pristup',
    noAccessBody: 'Prijavljeni ste kao {name}, ali ova stranica još nije dodijeljena vašem računu. Zamolite administratora da vam omogući pristup.',
  },
  sr: {
    tagline: 'Snimi · Mapiraj · Poveži',
    heroInfrastructure: 'Infrastruktura.',
    heroMeasured: 'Izmerena.',
    heroConnected: 'Povezana.',
    heroBody: 'Planirajte, merite i upravljajte optičkom infrastrukturom — od terenskih podataka do konačnog GIS izvoza.',
    loginSubtitle: 'Prijavite se svojim AU nalogom.',
    username: 'Korisničko ime',
    password: 'Lozinka',
    showPassword: 'Prikaži lozinku',
    hidePassword: 'Sakrij lozinku',
    keepSignedIn: 'Zapamti prijavu',
    forgotPassword: 'Zaboravljena lozinka?',
    forgotHelp: 'Lozinkama se upravlja u vašem AU nalogu — obratite se administratoru da vam je resetuje.',
    signIn: 'Prijava',
    signingIn: 'Prijavljivanje…',
    secureAccess: 'Siguran pristup za ovlašćene korisnike',
    whatsInside: 'Šta sadrži',
    capRtkTitle: 'RTK snimanje i mapiranje',
    capRtkBody: 'Terenska merenja na interaktivnoj mapi — trase, fiksne tačke i fotografije s gradilišta, s izmenama na licu mesta.',
    capAufmassTitle: 'Aufmaß tabela izveštaja',
    capAufmassBody: 'Izveštavanje po danima kolona uživo iz baze gradnje — količine, sati i ispravke.',
    capDashboardTitle: 'Pregledi i bonus',
    capDashboardBody: 'Poslovanje na prvi pogled — trendovi, najbolji projekti i kolone te kalkulator bonusa za radnike.',
    capExportTitle: 'GeoJSON, GeoPackage i PDF',
    capExportBody: 'Izvezite u potrebnom koordinatnom sistemu ili napravite izveštaj o iskopu po projektu — od terenskih podataka do konačnog GIS-a.',
    errTooMany: 'Previše pokušaja. Sačekajte minut i pokušajte ponovo.',
    errWrongCredentials: 'Pogrešno korisničko ime ili lozinka.',
    errServer: 'Greška servera pri prijavi ({status}). Nije do vaše lozinke.',
    errGeneric: 'Prijava nije uspela. Pokušajte ponovo.',
    noAccessTitle: 'Još nemate pristup',
    noAccessBody: 'Prijavljeni ste kao {name}, ali ova stranica još nije dodeljena vašem nalogu. Zamolite administratora da vam omogući pristup.',
  },
})
